import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSyncSceneIndexChanged } from '../../../../../PanoramaLibrary/hooks/useSyncSceneIndexChanged';
import { useSyncTourGuidChanged } from '../../../../../PanoramaLibrary/hooks/useSyncTourGuidChanged';
import { getItemByIdTypedSelector } from '../../../../store/reducers/items/itemsSelectors';
import { useStudentSelector } from '../../../../store/selectors/useStudentSelector';
import { isHotspotCompletedSelector } from '../../../tracking/services/CompletionService';
import { HotspotMapCompletedToast } from './HotspotMapCompletedToast';
import { HotspotMapRemainingToast } from './HotspotMapRemainingToast';
import { isHotspot } from '@ael/core-api';
import { IPanorama } from '@ael/core-api';
import { List } from 'immutable';
import { CSSTransition } from 'react-transition-group';

import './HotspotMapCounter.scss';

type HotspotMapCounterProps = {
  panorama: IPanorama;
  hotspots: List<string>;
  isCompleted: boolean;
};

export const HotspotMapCounter = (props: HotspotMapCounterProps) => {
  // This indicates whether no Hotspot's content is displayed thus toasts can be displayed
  const isHotspotModalShowing = useStudentSelector((state) => state.currentHotspot.hotspot);
  const isStartingMessageShowing = !useStudentSelector((state) => state.menuStartingMessage.dismissed);
  const canDisplayToast = isHotspotModalShowing || isStartingMessageShowing ? false : true;

  // Thanks to this we will not render congratulation message when it was once displayed and we returned to Hotspot map
  const [wasCompletedBeforeThisGotMounted] = useState(props.isCompleted);

  // Once we display completed toast then it is hidden after some time, and we do not unmount
  // the component so that we will not display completed toast every time the selector triggers re-render
  return (
    <>
      {!props.isCompleted && <HotspotMapCounterCore {...props} isPlaceForToastAvailable={canDisplayToast} />}
      <OneTimeShownHotspotMapCompletionToast
        isPlaceForToast={canDisplayToast && props.isCompleted && !wasCompletedBeforeThisGotMounted}
      />
    </>
  );
};

const OneTimeShownHotspotMapCompletionToast = (props: { isPlaceForToast: boolean }) => {
  const [shouldDisplay, setShouldDisplay] = useState(true);

  // There is no going back to set it on true, this is truly one time display
  useEffect(() => {
    if (!props.isPlaceForToast) {
      return;
    }

    const timer = setTimeout(() => {
      setShouldDisplay(false);
    }, 3000);

    return () => {
      // If timer exists, and we are again in cleanup, it means that message is to be hidden before 3 secs.
      // so we need to set shouldDisplay to false otherwise the local state will be still true and message might
      // will get rendered again when there will be place for it
      if (timer) {
        setShouldDisplay(false);
      }
      clearTimeout(timer);
    };
  }, [props.isPlaceForToast]);

  const completionToastRef = useRef<HTMLDivElement>(null);
  return (
    <CSSTransition
      nodeRef={completionToastRef}
      in={props.isPlaceForToast && shouldDisplay}
      classNames="bounce-from-top"
      timeout={400}
      unmountOnExit
    >
      <div ref={completionToastRef} className="hotspot-map-counter__toast-wrapper">
        <HotspotMapCompletedToast />
      </div>
    </CSSTransition>
  );
};

type HotspotMapCounterCoreProps = {
  isPlaceForToastAvailable: boolean;
} & Pick<HotspotMapCounterProps, 'hotspots' | 'panorama'>;
const HotspotMapCounterCore: React.FC<HotspotMapCounterCoreProps> = ({
  panorama,
  hotspots,
  isPlaceForToastAvailable,
}) => {
  const getItemByIdTyped = useStudentSelector(getItemByIdTypedSelector);
  const isHotspotCompleted = useStudentSelector(isHotspotCompletedSelector);
  const activeTourSceneGuid = useSyncTourGuidChanged(panorama);
  const activeTourSceneIndex = useSyncSceneIndexChanged(panorama);

  const [shouldToastDisplayWhenThereIsPlaceForIt, setShouldToastDisplayWhenThereIsPlaceForIt] = useState(true);

  // Count remaining hotspots on current scene
  const count = useMemo(() => {
    return hotspots
      .map((hotspotId: string) => getItemByIdTyped(hotspotId, isHotspot))
      .count(
        (hotspot) =>
          hotspot.position.sceneIndex === activeTourSceneIndex &&
          hotspot.position.tourSceneGuid === activeTourSceneGuid &&
          !isHotspotCompleted(hotspot)
      );
  }, [getItemByIdTyped, hotspots, isHotspotCompleted, activeTourSceneIndex, activeTourSceneGuid]);

  // This handles a minor edge case in which toast needs to be hidden before 3 sec timeout
  // finishes in those case proper cleanup did not take place
  useEffect(() => {
    if (!isPlaceForToastAvailable) {
      setShouldToastDisplayWhenThereIsPlaceForIt(false);
    }
    return;
  }, [isPlaceForToastAvailable]);

  // Prepare counter to be displayed when one of those changes
  // counter will eventually be displayed when there is place for it
  // a) number of completed hotspots changed
  // b) scene is changed
  useEffect(() => {
    setShouldToastDisplayWhenThereIsPlaceForIt(true);
    return;
  }, [count, activeTourSceneIndex, activeTourSceneGuid]);

  // When toast should be displayed, and we have the space for it
  // start 3 sec timer after which it will be hidden even though there is place for it
  const isToastVisible = shouldToastDisplayWhenThereIsPlaceForIt && isPlaceForToastAvailable;
  useEffect(() => {
    if (!isToastVisible) {
      return;
    }

    const timer = setTimeout(() => {
      setShouldToastDisplayWhenThereIsPlaceForIt(false);
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [isToastVisible, count, activeTourSceneGuid, activeTourSceneIndex]);

  const counterToastRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <CSSTransition
        nodeRef={counterToastRef}
        in={isToastVisible}
        classNames="bounce-from-top"
        timeout={400}
        unmountOnExit
      >
        <div ref={counterToastRef} className="hotspot-map-counter__toast-wrapper">
          <HotspotMapRemainingToast count={count} />
        </div>
      </CSSTransition>
    </>
  );
};
